import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * User Module Service Stores
 * Updated to match backend api-types
 */
export const userStores: ServiceStoreConfig[] = [
  {
    name: "user",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.userStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalUsers = entities.length;
          const activeUsers = entities.filter(
            (user: Record<string, unknown>) => user.active === true
          ).length;
          const adminUsers = entities.filter(
            (user: Record<string, unknown>) => user.isAdmin === true
          ).length;
          const recentLogins = entities.filter(
            (user: Record<string, unknown>) => {
              if (!user.lastLogin) return false;
              const lastLogin = new Date(user.lastLogin as string);
              const weekAgo = new Date();
              weekAgo.setDate(weekAgo.getDate() - 7);
              return lastLogin > weekAgo;
            }
          ).length;

          return [
            {
              title: "Total Users",
              value: () => totalUsers.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeUsers.toString(),
              valueColor: "success.main",
            },
            {
              title: "Admins",
              value: () => adminUsers.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Recent Logins (7d)",
              value: () => recentLogins.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
    },
  },
  {
    name: "user-certifications",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      // No table in this tenant (backend audit 2026-08-10): the service is not
      // configured server-side, so the boot find() 404s on every refresh and
      // login. Do not register it server-side to fix this — that turns a 404
      // into a 500, which is louder.
      disableInitialFind: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.userCertificationsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalCerts = entities.length;
          const activeCerts = entities.filter(
            (cert: Record<string, unknown>) => cert.status === "active"
          ).length;
          const expiringSoon = entities.filter(
            (cert: Record<string, unknown>) => {
              if (!cert.expiryDate) return false;
              const expiryDate = new Date(cert.expiryDate as string);
              const thirtyDaysFromNow = new Date();
              thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
              return expiryDate <= thirtyDaysFromNow && expiryDate > new Date();
            }
          ).length;
          const expired = entities.filter(
            (cert: Record<string, unknown>) => {
              if (!cert.expiryDate) return false;
              const expiryDate = new Date(cert.expiryDate as string);
              return expiryDate < new Date();
            }
          ).length;

          return [
            {
              title: "Total Certifications",
              value: () => totalCerts.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeCerts.toString(),
              valueColor: "success.main",
            },
            {
              title: "Expiring Soon (30d)",
              value: () => expiringSoon.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Expired",
              value: () => expired.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "user-permissions",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
    },
  },
  {
    name: "user-subscription",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.userSubscriptionStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalSubscriptions = entities.length;
          const activeSubscriptions = entities.filter(
            (sub: Record<string, unknown>) => sub.status === "active"
          ).length;
          const trialSubscriptions = entities.filter(
            (sub: Record<string, unknown>) => sub.subscriptionType === "trial"
          ).length;
          const premiumSubscriptions = entities.filter(
            (sub: Record<string, unknown>) => sub.subscriptionType === "premium"
          ).length;

          return [
            {
              title: "Total Subscriptions",
              value: () => totalSubscriptions.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeSubscriptions.toString(),
              valueColor: "success.main",
            },
            {
              title: "Trial",
              value: () => trialSubscriptions.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Premium",
              value: () => premiumSubscriptions.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
    },
  },
  {
    name: "user-training",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      // No table in this tenant (backend audit 2026-08-10): the service is not
      // configured server-side, so the boot find() 404s on every refresh and
      // login. Do not register it server-side to fix this — that turns a 404
      // into a 500, which is louder.
      disableInitialFind: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.userTrainingStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalTraining = entities.length;
          const completed = entities.filter(
            (training: Record<string, unknown>) => training.status === "completed"
          ).length;
          const inProgress = entities.filter(
            (training: Record<string, unknown>) => training.status === "in_progress"
          ).length;
          const notStarted = entities.filter(
            (training: Record<string, unknown>) => training.status === "not_started"
          ).length;

          return [
            {
              title: "Total Training",
              value: () => totalTraining.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Completed",
              value: () => completed.toString(),
              valueColor: "success.main",
            },
            {
              title: "In Progress",
              value: () => inProgress.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Not Started",
              value: () => notStarted.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
];
